

import { useState, useEffect, useCallback, useRef } from "react";

const API      = "/api/admin";
const ACCESS   = "/api/admin/access";
const getToken = () => localStorage.getItem("th_token") || localStorage.getItem("token") || "";
const authHdr  = () => ({ Authorization: `Bearer ${getToken()}` });
const fmtDate  = d => { try { return d ? new Date(d).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : "—"; } catch { return "—"; } };

/* ─── tiny helpers ─────────────────────────────────────────── */
function Toast({ msg, type, onClose }) {
  useEffect(() => { if (!msg) return; const t = setTimeout(onClose,3800); return ()=>clearTimeout(t); },[msg]);
  if (!msg) return null;
  return (
    <div className={`toast ${type==="error"?"toast-error":"toast-success"}`}>
      <span style={{fontSize:18}}>{type==="error"?"⚠️":"✅"}</span>{msg}
    </div>
  );
}

function Spinner() {
  return <div style={{display:"flex",justifyContent:"center",padding:40}}>
    <div style={{width:36,height:36,border:"4px solid var(--border)",borderTopColor:"var(--brand)",borderRadius:"50%",animation:"spin .7s linear infinite"}}/>
  </div>;
}

function CopyBtn({ text }) {
  const [copied,setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),2000); }); };
  return (
    <button className="btn bo" style={{padding:"5px 10px",fontSize:11}} onClick={copy}>
      {copied ? "✓ Copied" : "📋 Copy"}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SECTION BADGE
═══════════════════════════════════════════════════════════════ */
const SECTION_META = {
  dashboard:    { icon:"📊", color:"#0284c7" },
  recruiters:   { icon:"🤝", color:"#16a34a" },
  jobs:         { icon:"🏢", color:"#263012" },
  candidates:   { icon:"👥", color:"#7c3aed" },
  reviews:      { icon:"⭐", color:"#263012" },
  transactions: { icon:"💳", color:"#0f766e" },
  analytics:    { icon:"📈", color:"#be185d" },
  database:     { icon:"🗄️", color:"#475569" },
  access:       { icon:"🔐", color:"#1d4ed8" },
};

const DEFAULT_SECTIONS = [
  { id: "dashboard", label: "Dashboard", icon: "📊", desc: "Overview stats and charts" },
  { id: "recruiters", label: "Recruiters", icon: "🤝", desc: "Manage recruiter accounts and plans" },
  { id: "jobs", label: "Jobs", icon: "🏢", desc: "View and manage all job postings" },
  { id: "candidates", label: "Candidates", icon: "👥", desc: "Browse and manage candidate profiles" },
  { id: "reviews", label: "Reviews", icon: "⭐", desc: "Moderate company reviews" },
  { id: "transactions", label: "Transactions", icon: "💳", desc: "View all billing and invoices" },
  { id: "analytics", label: "Analytics", icon: "📈", desc: "Platform analytics and insights" },
  { id: "database", label: "Database", icon: "🗄️", desc: "Raw database table browser" },
  { id: "access", label: "Access Control", icon: "🔐", desc: "Manage delegated access" },
];

function SectionBadge({ id }) {
  const m = SECTION_META[id] || { icon:"📌", color:"#888" };
  return (
    <span style={{
      display:"inline-flex",alignItems:"center",gap:4,
      background:m.color+"18",color:m.color,
      border:`1px solid ${m.color}44`,borderRadius:6,
      padding:"2px 8px",fontSize:11,fontWeight:700
    }}>
      {m.icon} {id}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ACCESS CONTROL PAGE
═══════════════════════════════════════════════════════════════ */
export function AccessControlPage({ flash }) {
  const [grants,    setGrants]    = useState([]);
  const [sections,  setSections]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(true);
  const [viewLog,   setViewLog]   = useState(null);   // grant id
  const [log,       setLog]       = useState([]);
  const [logLoading,setLogLoading]= useState(false);

  /* form state */
  const [form, setForm] = useState({
    email: "", sections: [], label: "", note: "", expiresInDays: ""
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [gRes, sRes] = await Promise.all([
        fetch(`${ACCESS}/grants`,   { headers:authHdr() }),
        fetch(`${ACCESS}/sections`, { headers:authHdr() }),
      ]);
      const gData = await gRes.json().catch(() => ({}));
      const sData = await sRes.json().catch(() => ({}));
      setGrants(Array.isArray(gData.grants) ? gData.grants : []);
      setSections(Array.isArray(sData.sections) && sData.sections.length ? sData.sections : DEFAULT_SECTIONS);
    } catch(e) { flash?.("Failed to load access data", "error"); }
    finally {
      setSections(prev => prev.length ? prev : DEFAULT_SECTIONS);
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  const loadLog = async (grantId) => {
    setViewLog(grantId); setLogLoading(true);
    try {
      const r = await fetch(`${ACCESS}/log/${grantId}`, { headers:authHdr() });
      const d = await r.json();
      setLog(d.log || []);
    } finally { setLogLoading(false); }
  };

  const toggleSection = (id) => {
    setForm(f => ({
      ...f,
      sections: f.sections.includes(id)
        ? f.sections.filter(s => s !== id)
        : [...f.sections, id]
    }));
  };

  const selectAll = () => setForm(f => ({ ...f, sections: sections.map(s => s.id) }));
  const clearAll  = () => setForm(f => ({ ...f, sections: [] }));

  const handleCreate = async () => {
    if (!form.email || !form.email.includes("@"))
      return flash?.("Valid email required", "error");
    if (!form.sections.length)
      return flash?.("Select at least one section", "error");

    setSaving(true);
    try {
      const r = await fetch(`${ACCESS}/grants`, {
        method:"POST", headers:{...authHdr(),"Content-Type":"application/json"},
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Failed");
      flash?.(d.message || "Access granted!");
      setShowForm(false);
      setForm({ email:"", sections:[], label:"", note:"", expiresInDays:"" });
      load();
    } catch(e) { flash?.(e.message, "error"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (id, currentVal) => {
    try {
      await fetch(`${ACCESS}/grants/${id}`, {
        method:"PATCH", headers:{...authHdr(),"Content-Type":"application/json"},
        body: JSON.stringify({ is_active: !currentVal }),
      });
      setGrants(gs => gs.map(g => g.id===id ? {...g,is_active:!currentVal} : g));
    } catch(e) { flash?.(e.message,"error"); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Permanently revoke this access?")) return;
    try {
      await fetch(`${ACCESS}/grants/${id}`, { method:"DELETE", headers:authHdr() });
      setGrants(gs => gs.filter(g => g.id!==id));
      flash?.("Access revoked");
    } catch(e) { flash?.(e.message,"error"); }
  };

  if (loading) return <Spinner/>;

  return (
    <div className="sf">
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:22}}>
        <div>
          <h2 style={{fontWeight:800,fontSize:20,letterSpacing:"-0.4px"}}>🔐 Access Control</h2>
          <p style={{fontSize:12,color:"var(--text-3)",marginTop:3}}>
            Grant specific people access to selected dashboard sections — no admin password needed.
          </p>
        </div>
        <button className="btn bp" onClick={()=>setShowForm(s=>!s)}>
          {showForm ? "✕ Cancel" : "➕ Grant Access"}
        </button>
      </div>

      <div className="stat-row" style={{marginBottom:22}}>
        <div className="sc">
          <div style={{fontSize:12,color:"var(--text-3)",fontWeight:700}}>Available sections</div>
          <div style={{fontSize:26,fontWeight:800,marginTop:6}}>{sections.length || DEFAULT_SECTIONS.length}</div>
        </div>
        <div className="sc">
          <div style={{fontSize:12,color:"var(--text-3)",fontWeight:700}}>Active grants</div>
          <div style={{fontSize:26,fontWeight:800,marginTop:6}}>{grants.filter(g => g.is_active && !g.isExpired).length}</div>
        </div>
        <div className="sc">
          <div style={{fontSize:12,color:"var(--text-3)",fontWeight:700}}>Paused grants</div>
          <div style={{fontSize:26,fontWeight:800,marginTop:6}}>{grants.filter(g => !g.is_active && !g.isExpired).length}</div>
        </div>
        <div className="sc">
          <div style={{fontSize:12,color:"var(--text-3)",fontWeight:700}}>Expired grants</div>
          <div style={{fontSize:26,fontWeight:800,marginTop:6}}>{grants.filter(g => g.isExpired).length}</div>
        </div>
      </div>

      {/* Create Form */}
      {(showForm || grants.length === 0) && (
        <div className="card" style={{padding:24,marginBottom:22,border:"2px solid var(--brand)",background:"linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,249,252,0.96))"}}>
          <h3 style={{fontWeight:800,fontSize:15,marginBottom:18}}>New Access Grant</h3>
          <p style={{fontSize:12,color:"var(--text-3)",marginBottom:16}}>
            Pick the sections you want to expose, enter the recipient email, and generate a share link from here.
          </p>

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(240px, 1fr))",gap:14,marginBottom:18}}>
            <div>
              <label className="fl">Email Address *</label>
              <input className="fi" type="email" placeholder="person@company.com"
                value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}/>
            </div>
            <div>
              <label className="fl">Label (optional)</label>
              <input className="fi" placeholder="e.g. Marketing Team" type="text"
                value={form.label} onChange={e=>setForm(f=>({...f,label:e.target.value}))}/>
            </div>
            <div>
              <label className="fl">Expires In (days)</label>
              <input className="fi" placeholder="Leave blank = never" type="number" min="1"
                value={form.expiresInDays} onChange={e=>setForm(f=>({...f,expiresInDays:e.target.value}))}/>
            </div>
          </div>

          <div style={{marginBottom:18}}>
            <label className="fl">Note (optional)</label>
            <input className="fi" placeholder="Internal note about this access grant"
              value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))}/>
          </div>

          {/* Section picker */}
          <div style={{marginBottom:18}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <label className="fl" style={{margin:0}}>Sections to Grant *</label>
              <div style={{display:"flex",gap:8}}>
                <button className="btn bo" style={{padding:"4px 10px",fontSize:11}} onClick={selectAll}>Select All</button>
                <button className="btn bo" style={{padding:"4px 10px",fontSize:11}} onClick={clearAll}>Clear</button>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:8}}>
              {sections.map(s => {
                const sel = form.sections.includes(s.id);
                const m   = SECTION_META[s.id] || {};
                return (
                  <div key={s.id}
                    onClick={()=>toggleSection(s.id)}
                    style={{
                      padding:"10px 12px",borderRadius:10,cursor:"pointer",
                      border:`2px solid ${sel ? (m.color||"var(--brand)") : "var(--border)"}`,
                      background: sel ? (m.color||"var(--brand)")+"14" : "var(--surface-alt)",
                      transition:"all .15s",
                    }}>
                    <div style={{fontSize:18,marginBottom:4}}>{s.icon}</div>
                    <div style={{fontWeight:700,fontSize:12.5,color: sel ? (m.color||"var(--brand)") : "var(--text-1)"}}>{s.label}</div>
                    <div style={{fontSize:10.5,color:"var(--text-3)",marginTop:2}}>{s.desc}</div>
                  </div>
                );
              })}
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:14}}>
              {(form.sections.length ? form.sections : DEFAULT_SECTIONS.slice(0, 4).map(s => s.id)).map(id => (
                <SectionBadge key={id} id={id} />
              ))}
            </div>
          </div>

          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button className="btn bo" onClick={()=>setShowForm(false)}>Cancel</button>
            <button className="btn bp" onClick={handleCreate} disabled={saving}>
              {saving ? "Creating…" : `Grant Access to ${form.sections.length} section(s) →`}
            </button>
          </div>
        </div>
      )}

      {/* Activity Log Modal */}
      {viewLog && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setViewLog(null)}>
          <div className="modal-box" style={{width:680}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:18,paddingBottom:14,borderBottom:"1.5px solid var(--border-light)"}}>
              <h3 style={{fontWeight:800,fontSize:16}}>Activity Log</h3>
              <button className="btn bg" style={{fontSize:18}} onClick={()=>setViewLog(null)}>✕</button>
            </div>
            {logLoading ? <Spinner/> : log.length === 0 ? (
              <p style={{textAlign:"center",color:"var(--text-3)",padding:24}}>No activity recorded yet.</p>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {log.map((l,i) => (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 12px",borderRadius:8,background:"var(--surface-alt)",border:"1px solid var(--border-light)"}}>
                    <SectionBadge id={l.section}/>
                    <span style={{flex:1,fontSize:12.5}}>{l.action}</span>
                    <span style={{fontSize:11,color:"var(--text-3)"}}>{fmtDate(l.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Grants Table */}
      <div className="card">
        <div style={{padding:"14px 20px",borderBottom:"1.5px solid var(--border-light)",fontWeight:800,fontSize:13.5}}>
          🔐 Active Access Grants ({grants.length})
        </div>
        {grants.length === 0 ? (
          <div style={{padding:40,textAlign:"center",color:"var(--text-3)"}}>
            No access grants yet. Use the form above to share access with someone.
          </div>
        ) : grants.map(g => (
          <div key={g.id} style={{padding:"16px 20px",borderBottom:"1px solid var(--border-light)",background:g.isExpired?"#fff8f8":g.is_active?"":"var(--surface-alt)"}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:14,flexWrap:"wrap"}}>
              {/* Avatar + info */}
              <div style={{width:40,height:40,borderRadius:"50%",background:"var(--brand)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:14,flexShrink:0}}>
                {(g.email||"?")[0].toUpperCase()}
              </div>
              <div style={{flex:1,minWidth:200}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:5}}>
                  <span style={{fontWeight:700,fontSize:14}}>{g.email}</span>
                  {g.label && <span style={{fontSize:11,color:"var(--text-3)",background:"var(--surface-alt)",border:"1px solid var(--border)",borderRadius:6,padding:"1px 7px"}}>{g.label}</span>}
                  <span style={{
                    fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:99,
                    background: g.isExpired ? "#fee2e2" : g.is_active ? "#dcfce7" : "#fef3c7",
                    color:      g.isExpired ? "#263012" : g.is_active ? "#16a34a" : "#263012",
                  }}>
                    {g.isExpired ? "EXPIRED" : g.is_active ? "ACTIVE" : "PAUSED"}
                  </span>
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:6}}>
                  {(g.sections||[]).map(s => <SectionBadge key={s} id={s}/>)}
                </div>
                <div style={{display:"flex",gap:16,flexWrap:"wrap",fontSize:11,color:"var(--text-3)"}}>
                  <span>Created {fmtDate(g.created_at)}</span>
                  {g.expires_at && <span>Expires {fmtDate(g.expires_at)}</span>}
                  {g.last_used_at && <span>Last used {fmtDate(g.last_used_at)}</span>}
                </div>
              </div>

              {/* Share link */}
              <div style={{background:"var(--surface-alt)",border:"1px solid var(--border)",borderRadius:8,padding:"8px 12px",minWidth:280,maxWidth:380}}>
                <div style={{fontSize:10,fontWeight:700,color:"var(--text-3)",marginBottom:5,letterSpacing:".5px",textTransform:"uppercase"}}>Share Link</div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <code style={{fontSize:10.5,color:"var(--brand)",wordBreak:"break-all",flex:1}}>{g.shareUrl}</code>
                  <CopyBtn text={g.shareUrl}/>
                </div>
              </div>

              {/* Actions */}
              <div style={{display:"flex",flexDirection:"column",gap:6,flexShrink:0}}>
                <button className={g.is_active ? "btn bo" : "btn bs"} style={{padding:"5px 12px",fontSize:11}}
                  onClick={()=>toggleActive(g.id, g.is_active)}>
                  {g.is_active ? "⏸ Pause" : "▶ Resume"}
                </button>
                <button className="btn bo" style={{padding:"5px 12px",fontSize:11}}
                  onClick={()=>loadLog(g.id)}>
                  📋 Log
                </button>
                <button className="btn bd" style={{padding:"5px 12px",fontSize:11}}
                  onClick={()=>revoke(g.id)}>
                  🗑 Revoke
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   DATABASE BROWSER PAGE
═══════════════════════════════════════════════════════════════ */
export function DatabasePage({ flash }) {
  const [tables,     setTables]     = useState([]);
  const [tableSearch,setTableSearch]= useState("");
  const [selected,   setSelected]   = useState(null);
  const [tableData,  setTableData]  = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [rowsLoading,setRowsLoading]= useState(false);
  const [search,     setSearch]     = useState("");
  const [page,       setPage]       = useState(1);
  const [editCell,   setEditCell]   = useState(null); // {rowId, field, value, pkCol, pkVal}
  const [sqlMode,    setSqlMode]    = useState(false);
  const [sqlQuery,   setSqlQuery]   = useState("SELECT * FROM candidates LIMIT 20");
  const [sqlResult,  setSqlResult]  = useState(null);
  const [sqlRunning, setSqlRunning] = useState(false);
  const [sortCol,    setSortCol]    = useState("");
  const [sortDir,    setSortDir]    = useState("desc");
  const debounce = useRef(null);

  useEffect(() => {
    fetch(`${ACCESS}/database/tables`, { headers:authHdr() })
      .then(r=>r.json())
      .then(d=>{ setTables(d.tables||[]); setLoading(false); })
      .catch(()=>setLoading(false));
  }, []);

  const loadTable = useCallback(async (name, pg=1, q="", sc="", sd="desc") => {
    setRowsLoading(true);
    try {
      const params = new URLSearchParams({ page:pg, limit:50, search:q, sort:sc, dir:sd });
      const r = await fetch(`${ACCESS}/database/table/${name}?${params}`, { headers:authHdr() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setTableData(d);
      setPage(pg);
    } catch(e) { flash?.(e.message,"error"); }
    finally { setRowsLoading(false); }
  }, [flash]);

  const selectTable = (name) => {
    setSelected(name); setSearch(""); setPage(1); setSortCol(""); setSortDir("desc");
    loadTable(name);
  };

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      if (selected) loadTable(selected, 1, val, sortCol, sortDir);
    }, 350);
  };

  const handleSort = (col) => {
    const nd = sortCol===col && sortDir==="desc" ? "asc" : "desc";
    setSortCol(col); setSortDir(nd);
    if (selected) loadTable(selected, page, search, col, nd);
  };

  const startEdit = (row, field, pkCol) => {
    setEditCell({ rowId: row[pkCol], field, value: String(row[field]??""), pkCol });
  };

  const saveEdit = async () => {
    if (!editCell || !selected) return;
    try {
      const r = await fetch(`${ACCESS}/database/table/${selected}/${editCell.rowId}`, {
        method:"PATCH", headers:{...authHdr(),"Content-Type":"application/json"},
        body: JSON.stringify({ field: editCell.field, value: editCell.value }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      flash?.("Row updated");
      setEditCell(null);
      loadTable(selected, page, search, sortCol, sortDir);
    } catch(e) { flash?.(e.message,"error"); }
  };

  const deleteRow = async (pkVal) => {
    if (!window.confirm("Delete this row? This cannot be undone.")) return;
    try {
      await fetch(`${ACCESS}/database/table/${selected}/${pkVal}`, {
        method:"DELETE", headers:authHdr(),
      });
      flash?.("Row deleted");
      loadTable(selected, page, search, sortCol, sortDir);
    } catch(e) { flash?.(e.message,"error"); }
  };

  const runSql = async () => {
    setSqlRunning(true); setSqlResult(null);
    try {
      const r = await fetch(`${ACCESS}/database/query`, {
        method:"POST", headers:{...authHdr(),"Content-Type":"application/json"},
        body: JSON.stringify({ sql: sqlQuery }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setSqlResult(d);
    } catch(e) { flash?.(e.message,"error"); }
    finally { setSqlRunning(false); }
  };

  const filteredTables = tables.filter(t =>
    t.table.toLowerCase().includes(tableSearch.toLowerCase())
  );

  if (loading) return <Spinner/>;

  const pkCol = tableData?.columns?.find(c=>c.key==="PRI")?.name || "id";

  return (
    <div className="sf db-browser-container">
      {/* Left: table list */}
      <div className="card db-sidebar">
        <div style={{padding:"12px 14px",borderBottom:"1px solid var(--border-light)"}}>
          <div style={{fontWeight:800,fontSize:13,marginBottom:8}}>🗄️ Tables ({tables.length})</div>
          <input className="fi" style={{fontSize:12,padding:"6px 10px"}}
            placeholder="Filter tables…" value={tableSearch}
            onChange={e=>setTableSearch(e.target.value)}/>
        </div>
        <div style={{maxHeight:600,overflowY:"auto"}}>
          {filteredTables.map(t => (
            <div key={t.table}
              onClick={()=>selectTable(t.table)}
              style={{
                display:"flex",justifyContent:"space-between",alignItems:"center",
                padding:"8px 14px",cursor:"pointer",borderBottom:"1px solid var(--border-light)",
                background: selected===t.table ? "var(--brand-light)" : "transparent",
                color:      selected===t.table ? "var(--brand)" : "var(--text-1)",
                fontWeight: selected===t.table ? 700 : 400,
                fontSize:12.5,transition:"background .1s",
              }}>
              <span>{t.table}</span>
              <span style={{fontSize:10,color:"var(--text-3)",background:"var(--surface-alt)",borderRadius:4,padding:"1px 6px"}}>{Number(t.rows).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right: content */}
      <div style={{flex:1,minWidth:0}}>
        {/* SQL Mode toggle */}
        <div style={{display:"flex",gap:10,marginBottom:14}}>
          <button className={sqlMode?"btn bp":"btn bo"} onClick={()=>setSqlMode(s=>!s)}>
            {sqlMode ? "✕ Close SQL" : "💻 SQL Query"}
          </button>
          {selected && !sqlMode && (
            <span style={{alignSelf:"center",fontSize:13,fontWeight:700,color:"var(--text-2)"}}>
              📋 {selected} {tableData && `· ${Number(tableData.total).toLocaleString()} rows`}
            </span>
          )}
        </div>

        {/* SQL Panel */}
        {sqlMode && (
          <div className="card" style={{padding:18,marginBottom:18}}>
            <div style={{fontSize:12,color:"var(--text-3)",marginBottom:8}}>
              ⚠️ Only SELECT / SHOW / DESCRIBE queries are permitted.
            </div>
            <textarea
              className="fi" rows={4} value={sqlQuery}
              onChange={e=>setSqlQuery(e.target.value)}
              style={{fontFamily:"monospace",fontSize:12.5,marginBottom:10}}
            />
            <button className="btn bp" onClick={runSql} disabled={sqlRunning}>
              {sqlRunning ? "Running…" : "▶ Run Query"}
            </button>

            {sqlResult && (
              <div style={{marginTop:14,overflowX:"auto"}}>
                <div style={{fontSize:12,color:"var(--text-3)",marginBottom:8}}>
                  {sqlResult.count} row(s) returned
                </div>
                {sqlResult.rows?.length > 0 && (
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{background:"var(--surface-alt)"}}>
                        {Object.keys(sqlResult.rows[0]).map(k=>(
                          <th key={k} style={{padding:"6px 10px",borderBottom:"2px solid var(--border)",textAlign:"left",fontWeight:700,fontSize:11,color:"var(--text-3)",whiteSpace:"nowrap"}}>{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sqlResult.rows.slice(0,200).map((row,i)=>(
                        <tr key={i} style={{borderBottom:"1px solid var(--border-light)"}}>
                          {Object.values(row).map((val,j)=>(
                            <td key={j} style={{padding:"5px 10px",maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              {String(val??"")}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        {/* Table browser */}
        {selected && !sqlMode && (
          <>
            <div style={{display:"flex",gap:10,marginBottom:12,alignItems:"center"}}>
              <input className="fi" style={{maxWidth:320,fontSize:12.5,padding:"7px 12px"}}
                placeholder={`Search in ${selected}…`}
                value={search} onChange={e=>handleSearch(e.target.value)}/>
              {tableData && (
                <span style={{fontSize:11,color:"var(--text-3)",marginLeft:"auto"}}>
                  Page {page}/{tableData.pages}
                </span>
              )}
            </div>

            <div className="card" style={{overflowX:"auto"}}>
              {rowsLoading ? <Spinner/> : tableData && (
                <>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{background:"var(--surface-alt)"}}>
                        {tableData.columns.map(col=>(
                          <th key={col.name}
                            onClick={()=>handleSort(col.name)}
                            style={{
                              padding:"8px 10px",borderBottom:"2px solid var(--border)",
                              textAlign:"left",fontWeight:800,fontSize:10.5,
                              color: sortCol===col.name ? "var(--brand)" : "var(--text-3)",
                              cursor:"pointer",whiteSpace:"nowrap",letterSpacing:".5px",textTransform:"uppercase"
                            }}>
                            {col.name}
                            {sortCol===col.name && (sortDir==="asc"?" ↑":" ↓")}
                          </th>
                        ))}
                        <th style={{padding:"8px 10px",borderBottom:"2px solid var(--border)",textAlign:"center",fontWeight:800,fontSize:10.5,color:"var(--text-3)"}}>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.rows.map((row,i)=>(
                        <tr key={i} style={{borderBottom:"1px solid var(--border-light)",transition:"background .1s"}}
                          onMouseEnter={e=>e.currentTarget.style.background="#fafbff"}
                          onMouseLeave={e=>e.currentTarget.style.background=""}>
                          {tableData.columns.map(col=>{
                            const isEditing = editCell?.rowId===row[pkCol] && editCell?.field===col.name;
                            const raw = String(row[col.name]??"");
                            const disp = raw.length > 60 ? raw.slice(0,60)+"…" : raw;
                            return (
                              <td key={col.name} style={{padding:"6px 10px",maxWidth:180,verticalAlign:"middle"}}>
                                {isEditing ? (
                                  <div style={{display:"flex",gap:4}}>
                                    <input style={{border:"1.5px solid var(--brand)",borderRadius:5,padding:"3px 7px",fontSize:11.5,outline:"none",width:120}}
                                      value={editCell.value}
                                      onChange={e=>setEditCell(ec=>({...ec,value:e.target.value}))}
                                      onKeyDown={e=>{ if(e.key==="Enter") saveEdit(); if(e.key==="Escape") setEditCell(null); }}
                                      autoFocus/>
                                    <button className="btn bs" style={{padding:"3px 7px",fontSize:10}} onClick={saveEdit}>✓</button>
                                    <button className="btn bo" style={{padding:"3px 7px",fontSize:10}} onClick={()=>setEditCell(null)}>✕</button>
                                  </div>
                                ) : (
                                  <span
                                    title={raw}
                                    onDoubleClick={()=>startEdit(row, col.name, pkCol)}
                                    style={{
                                      display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                                      cursor:"text",fontSize:12,
                                      color: col.key==="PRI" ? "var(--text-3)" : "var(--text-1)"
                                    }}>
                                    {disp || <span style={{color:"var(--text-3)",fontStyle:"italic"}}>null</span>}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                          <td style={{padding:"4px 10px",textAlign:"center",whiteSpace:"nowrap"}}>
                            <button className="btn bd" style={{padding:"3px 8px",fontSize:10}}
                              onClick={()=>deleteRow(row[pkCol])}>🗑</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{padding:"8px 14px",fontSize:11,color:"var(--text-3)"}}>
                    💡 Double-click any cell to edit it inline. Press Enter to save, Esc to cancel.
                  </div>
                </>
              )}
            </div>

            {/* Pagination */}
            {tableData && tableData.pages > 1 && (
              <div style={{display:"flex",justifyContent:"center",gap:8,marginTop:14}}>
                <button className="btn bo" disabled={page===1}
                  onClick={()=>loadTable(selected,page-1,search,sortCol,sortDir)}>← Prev</button>
                {Array.from({length:Math.min(tableData.pages,7)},(_,i)=>i+1).map(p=>(
                  <button key={p} className={`btn ${p===page?"bp":"bo"}`}
                    onClick={()=>loadTable(selected,p,search,sortCol,sortDir)}>{p}</button>
                ))}
                <button className="btn bo" disabled={page===tableData.pages}
                  onClick={()=>loadTable(selected,page+1,search,sortCol,sortDir)}>Next →</button>
              </div>
            )}
          </>
        )}

        {!selected && !sqlMode && (
          <div style={{padding:60,textAlign:"center",color:"var(--text-3)"}}>
            <div style={{fontSize:40,marginBottom:12}}>🗄️</div>
            <div style={{fontWeight:700,fontSize:15}}>Select a table to browse its data</div>
            <div style={{fontSize:12,marginTop:6}}>Or use the SQL query panel for custom queries</div>
          </div>
        )}
      </div>
      <style>{`
        .db-browser-container { display: flex; gap: 18; align-items: flex-start; }
        .db-sidebar { width: 220px; flex-shrink: 0; }
        @media (max-width: 992px) {
          .db-browser-container { flex-direction: column; }
          .db-sidebar { width: 100%; max-height: 300px; }
        }
        .card { box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06); border-radius: 12px; }
        .fi:focus { border-color: var(--brand); box-shadow: 0 0 0 2px var(--brand-light); outline: none; }
        .btn { transition: transform 0.1s, opacity 0.1s; }
        .btn:active { transform: scale(0.98); }
      `}</style>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SHARED ACCESS GATE
   This is the page non-admin users land on when they open
   the shared link: /admin/shared?token=xxx
═══════════════════════════════════════════════════════════════ */
export function SharedAccessGate() {
  const [status, setStatus]  = useState("loading"); // loading | email_mismatch | ok | error
  const [error,  setError]   = useState("");
  const [data,   setData]    = useState(null);
  const [toast,  setToast]   = useState({ msg: "", type: "success" });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token  = params.get("token");

    if (!token) {
      setStatus("error");
      setError("No access token found in URL.");
      return;
    }

    // ── Step 1: get the logged-in user's email from localStorage ──
    let loggedInEmail = "";
    try {
      const raw =
        localStorage.getItem("th_user") ||
        localStorage.getItem("th_employer") ||
        localStorage.getItem("user");
      const parsed = raw ? JSON.parse(raw) : null;
      loggedInEmail = (parsed?.email || "").toLowerCase().trim();
    } catch { /* ignore */ }

    // ── Step 2: verify the token with the backend ──
    fetch(`${ACCESS}/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then(r => r.json())
      .then(d => {
        if (!d.success) throw new Error(d.error || "Invalid token");

        const grantedEmail = (d.email || "").toLowerCase().trim();

        // ── Step 3: check email match ──
        if (!loggedInEmail) {
          // Not logged in at all
          setStatus("error");
          setError(
            `This link was shared with ${grantedEmail}. ` +
            `Please log in with that email address to access this dashboard.`
          );
          return;
        }

        if (loggedInEmail !== grantedEmail) {
          // Logged in but wrong account
          setStatus("email_mismatch");
          setError(
            `You are logged in as ${loggedInEmail}, but this link was shared with ${grantedEmail}. ` +
            `Please switch to the correct account.`
          );
          return;
        }

        // ── Step 4: all good — store scoped token and proceed ──
        localStorage.setItem("th_token", d.scopedToken);
        setData(d);
        setStatus("ok");
      })
      .catch(e => {
        setError(e.message);
        setStatus("error");
      });
  }, []);

  // ── Loading ──
  if (status === "loading") return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", flexDirection: "column", gap: 16, fontFamily: "IBM Plex Sans, sans-serif" }}>
      <div style={{ width: 48, height: 48, border: "4px solid #e8eaef", borderTopColor: "#1d240e", borderRadius: "50%", animation: "spin .7s linear infinite" }} />
      <p style={{ color: "#8f96a3", fontWeight: 600 }}>Verifying access link…</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );

  // ── Wrong email (special UI with switch account hint) ──
  if (status === "email_mismatch") return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "IBM Plex Sans, sans-serif", background: "#f8fafc" }}>
      <div style={{ textAlign: "center", maxWidth: 480, padding: 40, background: "#fff", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,.08)" }}>
        <div style={{ fontSize: 52, marginBottom: 16 }}>🔀</div>
        <h2 style={{ fontWeight: 800, fontSize: 22, color: "#0d1117", marginBottom: 10 }}>Wrong Account</h2>
        <p style={{ color: "#4a5568", lineHeight: 1.7, marginBottom: 20 }}>{error}</p>
        <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "12px 16px", fontSize: 13, color: "#92400e", marginBottom: 20 }}>
          ⚠️ Log out and log back in with the correct email, then open this link again.
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button
            onClick={() => {
              // Clear stored user and redirect to login
              ["th_token", "th_employer_token", "th_user", "th_employer", "token", "user"]
                .forEach(k => localStorage.removeItem(k));
              window.location.href = `/employee/login?redirect=${encodeURIComponent(window.location.href)}`;
            }}
            style={{ padding: "10px 20px", background: "#1d240e", color: "#fff", border: "none", borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: "pointer" }}
          >
            Switch Account →
          </button>
        </div>
      </div>
    </div>
  );

  // ── Error ──
  if (status === "error") return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "IBM Plex Sans, sans-serif" }}>
      <div style={{ textAlign: "center", maxWidth: 440, padding: 40 }}>
        <div style={{ fontSize: 52, marginBottom: 16 }}>🔒</div>
        <h2 style={{ fontWeight: 800, fontSize: 22, color: "#0d1117", marginBottom: 10 }}>Access Denied</h2>
        <p style={{ color: "#4a5568", lineHeight: 1.7, marginBottom: 20 }}>{error}</p>
        <p style={{ color: "#8f96a3", fontSize: 13 }}>Contact the admin who shared this link for help.</p>
        <button
          onClick={() => window.location.href = `/employee/login?redirect=${encodeURIComponent(window.location.href)}`}
          style={{ marginTop: 16, padding: "10px 20px", background: "#0d1117", color: "#fff", border: "none", borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: "pointer" }}
        >
          Log In →
        </button>
      </div>
    </div>
  );

  // ── Success ──
  const flash = (msg, type = "success") => setToast({ msg, type });

  return (
    <div style={{ fontFamily: "IBM Plex Sans, sans-serif" }}>
      <Toast msg={toast.msg} type={toast.type} onClose={() => setToast({ msg: "", type: "success" })} />

      {/* Top banner */}
      <div style={{ background: "#0d1117", color: "#fff", padding: "10px 24px", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 28, height: 28, background: "linear-gradient(135deg,#1d240e,#263012)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14 }}>
          A
        </div>
        <div>
          <span style={{ fontWeight: 800, fontSize: 14 }}>TalentHive Admin</span>
          <span style={{ fontSize: 11, color: "rgba(255,255,255,.45)", marginLeft: 10 }}>
            Shared Access · {data.email}
          </span>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {data.sectionMeta.map(s => (
            <span key={s.id} style={{ background: "rgba(255,255,255,.1)", color: "rgba(255,255,255,.8)", padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
              {s.icon} {s.label}
            </span>
          ))}
        </div>
      </div>

      <SharedAdminPanel sections={data.sections} email={data.email} flash={flash} />
    </div>
  );
}

/* ─── Minimal admin panel that only shows allowed sections ─── */
function SharedAdminPanel({ sections, email, flash }) {
  const [page, setPage] = useState(sections[0] || "dashboard");

  const NAV_ALL = [
    { id:"dashboard",    icon:"📊", label:"Dashboard"    },
    { id:"recruiters",   icon:"🤝", label:"Recruiters"   },
    { id:"jobs",         icon:"🏢", label:"Jobs"         },
    { id:"candidates",   icon:"👥", label:"Candidates"   },
    { id:"reviews",      icon:"⭐", label:"Reviews"      },
    { id:"transactions", icon:"💳", label:"Transactions" },
    { id:"analytics",    icon:"📈", label:"Analytics"    },
    { id:"database",     icon:"🗄️", label:"Database"     },
  ];

  const NAV = NAV_ALL.filter(n => sections.includes(n.id));

  return (
    <div style={{display:"flex",height:"calc(100vh - 49px)",overflow:"hidden",background:"#f2f4f8"}}>
      {/* Mini sidebar */}
      <div style={{width:180,background:"#0d1117",display:"flex",flexDirection:"column",padding:"12px 10px",gap:2}}>
        {NAV.map(n => (
          <div key={n.id}
            onClick={()=>setPage(n.id)}
            style={{
              display:"flex",alignItems:"center",gap:10,padding:"9px 11px",
              borderRadius:9,cursor:"pointer",color: page===n.id ? "#fff" : "rgba(255,255,255,.5)",
              background: page===n.id ? "linear-gradient(135deg,#1d240e,#263012)" : "transparent",
              fontWeight: page===n.id ? 700 : 500, fontSize:13,
              transition:"all .15s",
            }}>
            <span>{n.icon}</span>
            <span>{n.label}</span>
          </div>
        ))}
      </div>

      {/* Content — dynamically import actual pages */}
      <div style={{flex:1,overflow:"auto",padding:24}}>
        <SharedPageRenderer pageId={page} flash={flash}/>
      </div>
    </div>
  );
}

/* Renders admin page content for delegated users */
function SharedPageRenderer({ pageId, flash }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setData(null);
    const loadMap = {
      dashboard:    () => Promise.all([
        fetch(`${API}/dashboard`,    { headers:authHdr() }).then(r=>r.json()),
        fetch(`${API}/recruiters`,   { headers:authHdr() }).then(r=>r.json()),
        fetch(`${API}/jobs`,         { headers:authHdr() }).then(r=>r.json()),
        fetch(`${API}/transactions`, { headers:authHdr() }).then(r=>r.json()),
      ]).then(([stats,recruiters,jobs,txns]) => ({ stats, recruiters:recruiters||[], jobs:jobs||[], transactions:txns||[] })),
      recruiters:   () => fetch(`${API}/recruiters`,   { headers:authHdr() }).then(r=>r.json()),
      jobs:         () => fetch(`${API}/jobs`,         { headers:authHdr() }).then(r=>r.json()),
      candidates:   () => fetch(`${API}/candidates`,   { headers:authHdr() }).then(r=>r.json()),
      reviews:      () => fetch(`${API}/reviews`,      { headers:authHdr() }).then(r=>r.json()),
      transactions: () => fetch(`${API}/transactions`, { headers:authHdr() }).then(r=>r.json()),
    };

    if (pageId === "database") {
      setLoading(false);
      setData("__db__");
      return;
    }

    const loader = loadMap[pageId];
    if (!loader) { setLoading(false); return; }

    loader()
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [pageId]);

  if (loading) return <Spinner/>;
  if (pageId === "database") return <DatabasePage flash={flash}/>;
  if (!data) return <div style={{textAlign:"center",padding:40,color:"var(--text-3)"}}>No data available.</div>;

  return (
    <div>
      <ReadOnlyBanner/>
      <SharedDataView pageId={pageId} data={data}/>
    </div>
  );
}

function ReadOnlyBanner() {
  return (
    <div style={{background:"#fef3c7",border:"1px solid #fcd34d",borderRadius:8,padding:"8px 14px",marginBottom:16,display:"flex",gap:8,alignItems:"center",fontSize:12.5}}>
      <span>👁️</span>
      <span style={{fontWeight:600,color:"#92400e"}}>You are viewing in read-only mode. Changes made here are visible data only.</span>
    </div>
  );
}

function SharedDataView({ pageId, data }) {
  const fmtMoney = n => "₹" + Number(n||0).toLocaleString("en-IN");
  const fmt      = n => Number(n||0).toLocaleString("en-IN");

  if (pageId === "dashboard") {
    const { stats={}, recruiters=[], jobs=[], transactions=[] } = data;
    return (
      <div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:14,marginBottom:22}}>
          {[
            {icon:"💰",label:"Revenue",    value:fmtMoney(stats.total_revenue||0),      color:"#16a34a"},
            {icon:"👥",label:"Candidates", value:fmt(stats.total_candidates||0),         color:"#7c3aed"},
            {icon:"🏢",label:"Active Jobs",value:fmt(stats.active_jobs||0),              color:"#1d240e"},
            {icon:"🤝",label:"Recruiters", value:fmt(stats.active_employers||0),         color:"#0284c7"},
            {icon:"🚀",label:"Applications",value:fmt(stats.total_applications||0),      color:"#be185d"},
          ].map(s=>(
            <div key={s.label} className="card" style={{padding:16}}>
              <div style={{fontSize:22,marginBottom:6}}>{s.icon}</div>
              <div style={{fontSize:22,fontWeight:800,color:s.color}}>{s.value}</div>
              <div style={{fontSize:11,color:"var(--text-3)"}}>{s.label}</div>
            </div>
          ))}
        </div>
        <div className="card">
          <div style={{padding:"12px 16px",fontWeight:800,fontSize:13,borderBottom:"1px solid var(--border-light)"}}>Recent Transactions</div>
          {transactions.slice(0,10).map((t,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:14,padding:"10px 16px",borderBottom:"1px solid var(--border-light)",fontSize:12.5}}>
              <span style={{flex:1,fontWeight:600}}>{t.company}</span>
              <span style={{color:"var(--text-3)"}}>{t.planName}</span>
              <span style={{fontWeight:800,color:"#16a34a"}}>{fmtMoney(t.total)}</span>
              <span style={{fontSize:10,padding:"2px 8px",borderRadius:99,background:t.status==="paid"?"#dcfce7":"#fee2e2",color:t.status==="paid"?"#16a34a":"#263012",fontWeight:700}}>
                {t.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const rows = Array.isArray(data) ? data : (data.reviews || data.data || []);

  if (!rows.length) return <div style={{textAlign:"center",padding:40,color:"var(--text-3)"}}>No data found.</div>;

  const cols = Object.keys(rows[0]).slice(0, 8);

  return (
    <div className="card" style={{overflowX:"auto"}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
        <thead>
          <tr style={{background:"var(--surface-alt)"}}>
            {cols.map(c=>(
              <th key={c} style={{padding:"8px 12px",borderBottom:"2px solid var(--border)",textAlign:"left",fontWeight:700,fontSize:10.5,color:"var(--text-3)",textTransform:"uppercase",whiteSpace:"nowrap"}}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0,100).map((row,i)=>(
            <tr key={i} style={{borderBottom:"1px solid var(--border-light)"}}>
              {cols.map(c=>(
                <td key={c} style={{padding:"7px 12px",maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:12.5}}>
                  {String(row[c]??"")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 100 && (
        <div style={{padding:"8px 14px",fontSize:11,color:"var(--text-3)"}}>
          Showing first 100 of {rows.length} rows.
        </div>
      )}
    </div>
  );
}

export default AccessControlPage;
